import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Color, ColorPalette } from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

interface Variables {
  colors: Partial<Color>[]
}

interface Props {
  brandVisualKitId: number
  id: number
  onSuccess?: () => void
}

interface Data {
  color_palette: ColorPalette
}

export const updateColorPalette = async ({
  id,
  workspaceId,
  brandVisualKitId,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(
    `/w/${workspaceId}/brand_visual_kits/${brandVisualKitId}/color_palettes/${id}`,
    {
      method: 'PATCH',
      body: { ...variables },
    },
  )

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update color palette', {
      metaData: {
        id,
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useUpdateColorPalette = ({ brandVisualKitId, id, onSuccess }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables>(
    ['workspaces', currentWorkspaceId, 'brand_visual_kits', brandVisualKitId, 'color_palettes', id],
    (variables) => {
      return updateColorPalette({ id, workspaceId: currentWorkspaceId, brandVisualKitId, ...variables })
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits'])
        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits'])
        onSuccess?.()
      },
    },
  )
}
